import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import api from "@/utils/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { fetchDistricts } from "../../store/slices/masterSlice";
import { useSidebar } from "@/components/ui/sidebar";
import { MultiSelect } from "../ui/multiselect";
import StatusBadge from "../common/StatusBadge";
import {
  setFilters,
  resetFilters,
} from "../../store/slices/submissionFilterSlice";

const SubmissionReview = () => {
  const { open } = useSidebar();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { districts, loading } = useSelector((state) => state.master);
  const reduxFilters = useSelector((state) => state.submissionFilter);

  const [data, setData] = useState([]);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const [localFilters, setLocalFilters] = useState(reduxFilters);

  const [pagination, setPagination] = useState({
    current_page: 1,
    pageSize: 10,
    totalPages: 1,
  });

  const [mainTabCounts, setMainTabCounts] = useState({
    all: 0,
    modernization: 0,
    new_shuttleless: 0,
    cfc_subsidy: 0,
    air_jet_loom: 0,
  });

  const [statusTabCounts, setStatusTabCounts] = useState({
    all: 0,
    submitted_for_provisional_sanction: 0,
    field_visit_completed: 0,
    provisional_sanction_recommended: 0,
    provisional_sanction_not_recommended: 0,
    submitted_for_approval: 0,
    after_purchase_field_visit_completed: 0,
    district_officer_approve: 0,
    district_officer_reject: 0,
    committee_approve: 0,
    committee_reject: 0,
  });

  const [eligibleTabCounts, setEligibleTabCounts] = useState({
    all: 0,
    eligible: 0,
    not_eligible: 0,
  });

  useEffect(() => {
    if (user?.role == "admin") {
      dispatch(fetchDistricts());
    }
  }, []);

  useEffect(() => {
    setLocalFilters(reduxFilters);
  }, [reduxFilters]);

  useEffect(() => {
    fetchData();
  }, [localFilters, pagination.current_page, pagination.pageSize, user]);

  const fetchData = async () => {
    try {
      setDataLoading(true);

      const params = new URLSearchParams({
        tab: localFilters.mainTab,
        status: localFilters.statusTab,
        current_page: pagination.current_page,
        per_page: pagination.pageSize,
        sort: localFilters.sortOrder,
        search: localFilters.searchTerm,
        from_date: localFilters.fromDate,
        to_date: localFilters.toDate,
        recommended_from_date: localFilters.recommendedFromDate,
        recommended_to_date: localFilters.recommendedToDate,
        eligibility: localFilters.eligibility,
      });

      if (localFilters.districts.length > 0) {
        params.append("districts", localFilters.districts.join(","));
      }

      const res = await api.get(
        `/district_wise_applications?${params.toString()}`
      );

      setData(res?.data?.data);
      setPagination({
        current_page: res?.data?.current_page,
        pageSize: res?.data?.per_page,
        totalPages: res?.data?.total_pages,
        total: res?.data?.total,
      });
      if (res?.data?.counts) {
        setMainTabCounts(res?.data?.counts?.main_tab);
        setStatusTabCounts(res?.data?.counts?.status_tab);
        setEligibleTabCounts(res?.data?.counts?.eligibility);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setLocalFilters((prev) => ({ ...prev, ...newFilters }));
    dispatch(setFilters({ ...localFilters, ...newFilters }));
  };

  const handleResetFilters = () => {
    const initialFilters = {
      statusTab: "all",
      mainTab: "all",
      eligibility: "all",
      districts: [],
      searchTerm: "",
      fromDate: "",
      toDate: "",
      recommendedFromDate: "",
      recommendedToDate: "",
      sortOrder: "none",
    };
    setLocalFilters(initialFilters);
    dispatch(resetFilters());
  };

  const openApproveModal = (submission) => {
    setSelectedSubmission(submission);
    setApproveModalOpen(true);
  };

  const openRejectModal = (submission) => {
    setSelectedSubmission(submission);
    setRejectModalOpen(true);
  };

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);
      await api.post("/district-admin/approve", {
        form_id: selectedSubmission?.id,
        form_type: selectedSubmission?.type,
      });
      toast({
        title: "Approved",
        description: "Submission approved successfully",
        variant: "success",
      });
      setApproveModalOpen(false);
      fetchData();
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Approval failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsSubmitting(true);
      const submitData = new FormData();
      submitData.append("remarks", remarks);
      submitData.append("form_id", selectedSubmission?.id);
      submitData.append("form_type", selectedSubmission?.type);

      await api.post("/district-admin/reject", submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast({
        title: "Rejected",
        description: "Submission rejected successfully",
        variant: "destructive",
      });
      setRejectModalOpen(false);
      setRemarks("");
      fetchData();
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Rejection failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Submission Review</h1>
      </div>

      <div>
        {/* Accordion Filter */}
        <Accordion type="single" collapsible className="mb-4">
          <AccordionItem value="filters">
            <AccordionTrigger className="bg-blue-500 p-4 font-semibold text-white">
              Advanced Filters
            </AccordionTrigger>
            <AccordionContent className="p-5 bg-blue-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">District Wise</label>
                  <MultiSelect
                    options={
                      user?.role == "district_admin"
                        ? user?.assigned_districts
                        : districts || []
                    }
                    value={localFilters.districts}
                    onChange={(selected) =>
                      handleFilterChange({ districts: selected })
                    }
                    placeholder="Select District"
                    disabled={
                      user?.role == "district_admin"
                        ? !user?.assigned_districts
                        : !districts || loading
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Application Type
                  </label>
                  <Select
                    value={localFilters.mainTab}
                    onValueChange={(v) => handleFilterChange({ mainTab: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        All ({mainTabCounts.all})
                      </SelectItem>
                      <SelectItem value="modernization">
                        Modernization ({mainTabCounts.modernization})
                      </SelectItem>
                      <SelectItem value="new_shuttleless">
                        New Shuttleless ({mainTabCounts.new_shuttleless})
                      </SelectItem>
                      <SelectItem value="cfc_subsidy">
                        CFC Subsidy ({mainTabCounts.cfc_subsidy})
                      </SelectItem>
                      {/* <SelectItem value="air_jet_loom">
                        Air Jet Loom ({mainTabCounts.air_jet_loom})
                      </SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select
                    value={localFilters.statusTab}
                    onValueChange={(v) => handleFilterChange({ statusTab: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        All ({statusTabCounts.all})
                      </SelectItem>
                      <SelectItem value="submitted_for_provisional_sanction">
                        Submitted For Provisional Sanction (
                        {statusTabCounts.submitted_for_provisional_sanction})
                      </SelectItem>
                      <SelectItem value="provisional_sanction_recommended">
                        Provisional Sanction Recommended (
                        {statusTabCounts.provisional_sanction_recommended})
                      </SelectItem>
                      <SelectItem value="provisional_sanction_not_recommended">
                        Provisional Sanction Not Recommended (
                        {statusTabCounts.provisional_sanction_not_recommended})
                      </SelectItem>
                      <SelectItem value="submitted_for_approval">
                        Application Submitted For Approval (
                        {statusTabCounts.submitted_for_approval})
                      </SelectItem>
                      <SelectItem value="after_purchase_field_visit_completed">
                        After Purchase Field Visit Completed (
                        {statusTabCounts.after_purchase_field_visit_completed})
                      </SelectItem>
                      <SelectItem value="district_officer_approve">
                        District Officer Approve (
                        {statusTabCounts.district_officer_approve})
                      </SelectItem>
                      <SelectItem value="district_officer_reject">
                        District Officer Reject (
                        {statusTabCounts.district_officer_reject})
                      </SelectItem>
                      <SelectItem value="committee_approve">
                        Committee Approved ({statusTabCounts.committee_approve})
                      </SelectItem>
                      <SelectItem value="committee_reject">
                        Committee Reject ({statusTabCounts.committee_reject})
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Eligibility Status
                  </label>
                  <Select
                    value={localFilters.eligibility}
                    onValueChange={(v) =>
                      handleFilterChange({ eligibility: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Order" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        All ({eligibleTabCounts.all})
                      </SelectItem>
                      <SelectItem value="eligible">
                        Eligible ({eligibleTabCounts.eligible})
                      </SelectItem>
                      <SelectItem value="not_eligible">
                        Not Eligible ({eligibleTabCounts.not_eligible})
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* From Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">From Date</label>
                  <Input
                    type="date"
                    value={localFilters.fromDate}
                    onChange={(e) => {
                      const from = e.target.value;
                      if (
                        localFilters.toDate &&
                        new Date(from) > new Date(localFilters.toDate)
                      ) {
                        toast({
                          title: "Invalid Date Range",
                          description: "From Date cannot be after To Date.",
                          variant: "destructive",
                        });
                        return;
                      }
                      handleFilterChange({ fromDate: from });
                    }}
                  />
                </div>

                {/* To Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">To Date</label>
                  <Input
                    type="date"
                    value={localFilters.toDate}
                    onChange={(e) => {
                      const to = e.target.value;
                      if (
                        localFilters.fromDate &&
                        new Date(to) < new Date(localFilters.fromDate)
                      ) {
                        toast({
                          title: "Invalid Date Range",
                          description: "To Date cannot be before From Date.",
                          variant: "destructive",
                        });
                        return;
                      }
                      handleFilterChange({ toDate: to });
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Sort by Priority (Max 11 Points)
                  </label>
                  <Select
                    value={localFilters.sortOrder}
                    onValueChange={(v) => handleFilterChange({ sortOrder: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Order" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="asc">0 to 11</SelectItem>
                      <SelectItem value="desc">11 to 0</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                   {/* Provisional Sanction Recommended From Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Provisional Sanction Recommended From Date
                  </label>
                  <Input
                    type="date"
                    value={localFilters.recommendedFromDate}
                    onChange={(e) => {
                      const from = e.target.value;
                      if (
                        localFilters.recommendedToDate &&
                        new Date(from) > new Date(localFilters.recommendedToDate)
                      ) {
                        toast({
                          title: "Invalid Date Range",
                          description: "From Date cannot be after To Date.",
                          variant: "destructive",
                        });
                        return;
                      }
                      handleFilterChange({ recommendedFromDate: from });
                    }}
                  />
                </div>

                {/* Provisional Sanction Recommended To Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Provisional Sanction Recommended To Date
                  </label>
                  <Input
                    type="date"
                    value={localFilters.recommendedToDate}
                    onChange={(e) => {
                      const to = e.target.value;
                      if (
                        localFilters.recommendedFromDate &&
                        new Date(to) < new Date(localFilters.recommendedFromDate)
                      ) {
                        toast({
                          title: "Invalid Date Range",
                          description: "To Date cannot be before From Date.",
                          variant: "destructive",
                        });
                        return;
                      }
                      handleFilterChange({ recommendedToDate: to });
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="space-y-2 w-full">
                  <label className="text-sm font-medium">Search</label>
                  <Input
                    placeholder="Search by Applicant or Application No"
                    value={localFilters.searchTerm}
                    onChange={(e) =>
                      handleFilterChange({ searchTerm: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4 px-2">
                <Button variant="outline" onClick={handleResetFilters}>
                  Reset
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Card className="bg-white relative w-full overflow-hidden">
          <CardHeader className="p-2" />
          <CardContent className="p-2">
            {dataLoading || loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div
                className={`relative ${
                  open ? "w-[79vw]" : "w-[96vw]"
                } overflow-x-auto`}
              >
                <Table className="min-w-[1200px] table-fixed">
                  <TableHeader className="bg-teal-700">
                    <TableRow className="hover:bg-teal-700">
                      <TableHead className="text-white w-[40px] shadow-[8px_0_4px_rgba(0,0,0,0.1)] font-bold sticky left-0 z-30 bg-teal-700">
                        #
                      </TableHead>
                      <TableHead className="text-white w-[200px] font-bold">
                        Application No
                      </TableHead>
                      <TableHead className="text-white w-[200px] font-bold">
                        District
                      </TableHead>
                      <TableHead className="text-white w-[200px] font-bold">
                        User
                      </TableHead>
                      <TableHead className="text-white w-[200px] font-bold">
                        Organization Type
                      </TableHead>
                      <TableHead className="text-white w-[200px] font-bold">
                        Submitted On
                      </TableHead>
                      <TableHead className="text-white w-[100px] shadow-[-2px_0_4px_rgba(0,0,0,0.1)] font-bold text-center sticky right-[350px] z-30 bg-teal-700 ">
                        Eligible
                      </TableHead>
                      <TableHead className="text-white w-[150px] font-bold">
                        Priority Points
                      </TableHead>
                      <TableHead className="text-white w-[100px] font-bold text-center sticky right-[250px] z-30 bg-teal-700 ">
                        No. of Loom to Upgrade / Purchase
                      </TableHead>
                      <TableHead className="text-white w-[150px] font-bold text-center sticky right-[100px] z-30 bg-teal-700 ">
                        Status
                      </TableHead>
                      <TableHead className="text-white w-[100px] font-bold text-center sticky right-0 z-30 bg-teal-700 ">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.length ? (
                      data.map((item, index) => (
                        <TableRow key={index} className="hover:bg-white-700">
                          <TableCell className="sticky left-0 w-[40px] shadow-[8px_0_4px_rgba(0,0,0,0.1)] bg-white">
                            {(pagination?.current_page - 1) *
                              pagination?.pageSize +
                              (index + 1)}
                          </TableCell>
                          <TableCell className="w-[200px]">
                            {item?.application_no || item?.application_number}
                          </TableCell>
                          <TableCell className="w-[200px]">
                            {item?.district?.name}
                          </TableCell>
                          <TableCell className="w-[200px]">
                            {item?.applicant_name || item?.user?.name}
                          </TableCell>
                          <TableCell className="w-[200px]">
                            {item?.user?.organization_type}
                          </TableCell>
                          <TableCell className="w-[200px]">
                            {item?.submitted_on ? (
                              <>
                                {new Date(item?.submitted_on)
                                  .toLocaleDateString("en-GB")
                                  .replace(/\//g, "-")}
                                <br />
                                {new Date(
                                  item?.submitted_on
                                ).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                              </>
                            ) : (
                              ""
                            )}
                          </TableCell>
                          <TableCell className="w-[100px] shadow-[-2px_0_4px_rgba(0,0,0,0.1)] sticky right-[350px] bg-white">
                            <Badge
                              className={`${
                                item?.is_eligible ? "bg-teal-600" : "bg-red-600"
                              } text-center`}
                            >
                              {item?.eligibility_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="w-[150px]">
                            {item?.priority_points}
                          </TableCell>
                          <TableCell className="w-[100px] shadow-[-2px_0_4px_rgba(0,0,0,0.1)] sticky right-[250px] bg-white">
                            {item?.no_of_looms_to_upgrade ??
                              item?.purchase_details?.no_of_loom_to_purchase ??
                              "-"}
                          </TableCell>
                          <TableCell className="w-[150px] sticky right-[100px] bg-white">
                            <StatusBadge status={item?.status} />
                          </TableCell>
                          <TableCell className="w-[100px] sticky right-0 bg-white">
                            <div className="flex space-x-2">
                              <Link
                                to={`/application/${item?.type}/${item?.id}`}
                              >
                                <Button variant="secondary" size="sm">
                                  View
                                </Button>
                              </Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="text-center text-muted-foreground py-8"
                        >
                          No Applications found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                <div className="flex justify-end mt-4">
                  <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.totalPages}
                    pageSize={pagination.pageSize}
                    onPageChange={(page) =>
                      setPagination((prev) => ({ ...prev, current_page: page }))
                    }
                    onPageSizeChange={(size) =>
                      setPagination((prev) => ({
                        ...prev,
                        pageSize: size,
                        current_page: 1,
                      }))
                    }
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to approve this submission?
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Approve"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <p>Enter remarks for rejection:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={isSubmitting || !remarks.trim()}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubmissionReview;
